import styled from "styled-components"

import {
  Select,
  spacing,
  Text,
  TextArea,
  TextField,
  TSelectOptions,
} from "@minuthq/meatball-ui-react"
import { FormikProps } from "formik"

import { useTaskTypeLabels } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import {
  TTaskTemplatePostBody,
  TTaskType,
} from "src/data/taskManagement/taskManagementTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Divider } from "src/ui/Divider/Divider"

export function TaskTemplateForm({
  form,
}: {
  form: FormikProps<TTaskTemplatePostBody>
}) {
  const { t, langKeys, tPlaceholder } = useTranslate()
  const taskTypes = useTaskTypeLabels()

  const taskCategory: TSelectOptions<TTaskType> = Object.entries(taskTypes).map(
    ([type, label]) => ({
      label,
      value: type as TTaskType,
    })
  )

  return (
    <Box>
      <Section>
        <Text variant="subtitle" marginBottom={spacing.XS2}>
          {tPlaceholder("Name your template")}
        </Text>

        <TextField
          placeholder={tPlaceholder("Template name*")}
          value={form.values.title}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              title: value,
            }))
          }}
          required
        />

        <TextArea
          placeholder={tPlaceholder("Template description")}
          value={form.values.description ?? ""}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              description: value,
            }))
          }}
          minRows={3}
          maxRows={5}
        />
      </Section>

      <Divider />

      <Section>
        <Content>
          <Text variant="subtitle" marginBottom={spacing.XS2}>
            {tPlaceholder("Task type, title and description")}
          </Text>

          <Text variant="bodyS" color="secondary">
            {tPlaceholder(
              "This type, title and description will be automatically applied to all tasks using this template."
            )}
          </Text>
        </Content>
        <FieldGroup>
          <Select
            placeholder={tPlaceholder("Task type*")}
            value={form.values.task_category ?? ""}
            options={[
              {
                label: t(langKeys.task_management_select_a_task),
                value: "",
                disabled: true,
              },
              ...taskCategory,
            ]}
            onChange={(value) => {
              form.setValues((prev) => ({
                ...prev,
                task_category: value as TTaskType,
              }))
            }}
            required
          />
          <TextField
            placeholder={tPlaceholder("Task title*")}
            value={form.values.task_title}
            onChange={(value) => {
              form.setValues((prev) => ({
                ...prev,
                task_title: value,
              }))
            }}
            required
          />
          <TextArea
            placeholder={tPlaceholder("Task description")}
            value={form.values.task_description ?? ""}
            onChange={(value) => {
              form.setValues((prev) => ({
                ...prev,
                task_description: value,
              }))
            }}
            minRows={3}
            maxRows={5}
          />
        </FieldGroup>
      </Section>
    </Box>
  )
}

const Box = styled.div`
  display: grid;
  gap: ${spacing.L};
`

const Section = styled.div`
  display: grid;
  gap: ${spacing.M};
`

const Content = styled.div`
  display: grid;
  gap: ${spacing.XS2};
`

const FieldGroup = styled.div`
  display: grid;
  gap: ${spacing.M};
`
